import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { useTheme } from '../../hooks/useTheme';

import { NAV_THEMES } from './nav-tokens';
import type { NavCollapseState } from './navCollapse';

interface NavPanelToggleProps {
  /** Whether the panel is labelled in THIS breakpoint band. */
  expanded: boolean;
  onSet: (next: NavCollapseState) => void;
  /** Which band this instance belongs to. */
  className: string;
  /**
   * Where it sits. `header` is the chip beside the wordmark; `rail` stands in
   * a column of 40px icon rows and has to match their box exactly, or its
   * glyph sits off the line the rest of the column keeps.
   */
  placement?: 'header' | 'rail';
}

/** The collapse/expand control. Always writes an explicit choice, so the
 *  preference then holds at every width rather than snapping back to the
 *  breakpoint default. */
export default function NavPanelToggle({
  expanded,
  onSet,
  className,
  placement = 'header',
}: NavPanelToggleProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const Icon = expanded ? PanelLeftClose : PanelLeftOpen;
  const action = expanded ? 'Collapse navigation' : 'Expand navigation';
  const inRail = placement === 'rail';
  return (
    <button
      type="button"
      onClick={() => onSet(expanded ? 'collapsed' : 'expanded')}
      aria-expanded={expanded}
      aria-label={action}
      title={action}
      className={`shrink-0 cursor-pointer items-center justify-center border-0 ${
        inRail ? 'h-10 w-10 rounded-[10px]' : 'h-8 w-8 rounded-lg'
      } ${className}`}
      style={{ background: inRail ? 'transparent' : t.surface, color: t.muted }}
    >
      <Icon size={inRail ? 18 : 16} aria-hidden />
    </button>
  );
}
